import { Piece } from 'src/services/pieces';
import { getPiece } from './configurator.utils';
import {
  getBoxOfCustomElement as getBoxOfPlating,
  getBoxOfCustomElement as getBoxOfSole,
  getBoxOfCustomElement as getBoxOfBox,
} from './common.utils';
import { Box3 } from 'three';
import { CustomStele } from 'src/services/stock';

export const checkMinMax = (
  value: number,
  granit: { minLength: string; maxLength: string },
  setError: (error: string) => void,
) => {
  if (value < Number.parseFloat(granit.minLength) / 100)
    return Number.parseFloat(granit.minLength) / 100;
  if (value > Number.parseFloat(granit.maxLength) / 100) {
    setError(
      `Attention, votre pièce dépasse la taille maximale de ce granit (${Number.parseFloat(
        granit.maxLength,
      )}). Elle sera coupée.`,
    );
  }
  return value;
};

export const getIndexType = (
  index: number,
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[] = [],
) => {
  if (
    index >= activePieces.length &&
    index < activePieces.length + customSteles.length
  ) {
    return 'customStele';
  }
  if (
    index >= activePieces.length + customSteles.length &&
    index < activePieces.length + customSteles.length + customPlatings.length
  ) {
    return 'customPlating';
  }
  if (
    index >=
      activePieces.length + customSteles.length + customPlatings.length &&
    index <
      activePieces.length +
        customSteles.length +
        customPlatings.length +
        customSoles.length
  ) {
    return 'customSole';
  }
  if (
    index >=
      activePieces.length +
        customSteles.length +
        customPlatings.length +
        customSoles.length &&
    index <
      activePieces.length +
        customSteles.length +
        customPlatings.length +
        customSoles.length +
        customBoxes.length
  ) {
    return 'customBox';
  }
  return 'activePieces';
};

export const getLocalIndexName = (
  index: number,
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[] = [],
) => {
  const localIndex = getLocalIndex(
    index,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  if (localIndex === 0) {
    return 'avant';
  }
  if (localIndex === 1) {
    return 'arrière';
  }
  if (localIndex === 2) {
    return 'gauche';
  }
  if (localIndex === 3) {
    return 'droit';
  }
};

export const getLocalIndex = (
  index: number,
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[] = [],
) => {
  const type = getIndexType(
    index,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  if (type === 'customStele') {
    return index - activePieces.length;
  }
  if (type === 'customPlating') {
    return index - activePieces.length - customSteles.length;
  }
  if (type === 'customSole') {
    return (
      index - activePieces.length - customSteles.length - customPlatings.length
    );
  }
  if (type === 'customBox') {
    return (
      index -
      activePieces.length -
      customSteles.length -
      customPlatings.length -
      customSoles.length
    );
  }
  return index;
};

export const getGlobalIndex = (
  index: number,
  type:
    | 'activePieces'
    | 'customStele'
    | 'customPlating'
    | 'customSole'
    | 'customBox',
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[] = [],
) => {
  if (type === 'customStele') {
    return activePieces.length;
  }
  if (type === 'customPlating') {
    return activePieces.length + customSteles.length;
  }
  if (type === 'customSole') {
    return activePieces.length + customSteles.length + customPlatings.length;
  }
  if (type === 'customBox') {
    return (
      activePieces.length +
      customSteles.length +
      customPlatings.length +
      customSoles.length
    );
  }
  return index;
};

export const getPieceAndGeometryWithIndex = (
  index: number,
  pieces: any[],
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[] = [],
) => {
  const piece = getPiece(
    index,
    pieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  let geomtryBox: number[] = [];

  const type = getIndexType(
    index,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  if (type === 'activePieces') {
    geomtryBox = piece?.geometry.attributes.position.array;
  } else if (type === 'customStele') {
    geomtryBox = piece?.geometry.attributes.position.array;
  } else if (type === 'customPlating') {
    const { geometryWithoutPosition } = getBoxOfPlating(piece);
    geomtryBox = geometryWithoutPosition;
  } else if (type === 'customSole') {
    const { geometryWithoutPosition } = getBoxOfSole(piece);
    geomtryBox = geometryWithoutPosition;
  } else if (type === 'customBox') {
    const { geometryWithoutPosition } = getBoxOfBox(piece);
    geomtryBox = geometryWithoutPosition;
  }

  return { geomtryBox, piece, type };
};

export const pieceHasBeenModified = (
  piece: Piece,
  history: { [k: string]: { [k: string]: Float32Array } },
) => {
  const historyOfPiece = history[piece.uuid];

  const originalBox = [];
  const newBox = [];

  for (const [key, value] of Object.entries(historyOfPiece || {})) {
    const keys = key.split(',');
    originalBox.push(Number(keys[0]), Number(keys[1]), Number(keys[2]));
    newBox.push(Number(value[0]), Number(value[1]), Number(value[2]));
  }

  const originalBox3 = new Box3().setFromArray(originalBox);
  const newBox3 = new Box3().setFromArray(newBox);

  const originalX = ((originalBox3.max.x - originalBox3.min.x) * 100).toFixed(
    2,
  );
  const originalY = ((originalBox3.max.y - originalBox3.min.y) * 100).toFixed(
    2,
  );
  const originalZ = ((originalBox3.max.z - originalBox3.min.z) * 100).toFixed(
    2,
  );

  const newX = ((newBox3.max.x - newBox3.min.x) * 100).toFixed(2);
  const newY = ((newBox3.max.y - newBox3.min.y) * 100).toFixed(2);
  const newZ = ((newBox3.max.z - newBox3.min.z) * 100).toFixed(2);

  return originalX !== newX || originalY !== newY || originalZ !== newZ;
};

export const isTwoSteleEqual = (stele1: CustomStele, stele2: CustomStele) => {
  return (
    stele1.baseIndex === stele2.baseIndex &&
    stele1.boxDownWidth === stele2.boxDownWidth &&
    stele1.boxHeight === stele2.boxHeight &&
    stele1.boxLeftHeight === stele2.boxLeftHeight &&
    stele1.boxRightHeight === stele2.boxRightHeight &&
    stele1.boxUpWidth === stele2.boxUpWidth &&
    stele1.headIndex === stele2.headIndex &&
    stele1.depth === stele2.depth
  );
};

export const getDimensionsWithGeometry = (array: Float32Array) => {
  const box = new Box3().setFromArray(array);
  return {
    x: Number(String(Math.round((box.max.x - box.min.x) * 100))),
    y: Number(String(Math.round((box.max.y - box.min.y) * 100))),
    z: Number(String(Math.round((box.max.z - box.min.z) * 100))),
  };
};

export const getSizeWithGeometry = (array: Float32Array) => {
  const { x, y, z } = getDimensionsWithGeometry(array);

  return x * y * z;
};
